// sim/enemyScaling.ts — POWER-TARGETED ENEMY SCALING: one deterministic policy that
// pins each enemy tier's strength to a band around the player colony's POWER, so the
// challenge stays relevant at every progression level instead of drifting loose of it.
//
// "Colony Power" here is the SIM-SIDE, deterministic measure already used for war and
// the leaderboard: RivalSystem.militaryStrength(cid) = population + soldiers + evolved
// Combat/Defense gene channels. (The display-only web/colonyPower.ts read-model is a
// genome-potential lens that is NEVER folded into the sim/hash, so it CANNOT drive a
// deterministic sim system; militaryStrength is its in-sim counterpart and the right
// input for combat scaling.) The player is colony PLAYER_COLONY (0).
//
// THE BANDS (target enemy strength as a % of player power):
//   Wildlife        80–120%   (the ambient tier — count tracks power, capped)
//   Rival Colonies  90–110%   (peer tier — evolution nudged to stay in band)
//   Apex Creatures 120–160%   (boss tier — hp pool scaled above the player)
//
// GUARANTEES (the two "never"s):
//   • NEVER hopelessly weak — player power is floored (MIN_REF_POWER) and every tier
//     has an absolute floor, so a fledgling colony still meets a real ecosystem.
//   • NEVER impossible — every tier has an absolute ceiling (the wildlife head-count
//     cap, the apex hp multiplier cap), so scaling can't run away into an unbeatable
//     swarm / boss even at extreme player power.
//
// VARIANCE: each sample draws ONE int from the CALLER's own (stream-isolated) Rng and
// jitters uniformly across the band, so a tier "breathes" within its band rather than
// sitting on a single ratio. Pure integer arithmetic; deterministic given the Rng.
//
// DETERMINISM: every function here is a pure function of its integer inputs + the Rng
// the caller threads in (the wildlife / apex / rival stream). It owns no state, draws
// no ambient RNG, and allocates nothing. Folding the results into each system stays the
// caller's job (see ecosystem.ts / apex.ts / rivals.ts).

import type { Rng } from '../rng.ts';

/** An inclusive percent band: target strength ∈ [minPct, maxPct]% of player power. */
export interface PowerBand {
  readonly minPct: number;
  readonly maxPct: number;
}

/** Wildlife: the ambient tier sits a touch below to a touch above the player. */
export const WILDLIFE_BAND: PowerBand = { minPct: 80, maxPct: 120 };
/** Rival colonies: peers, kept tight around parity. */
export const RIVAL_BAND: PowerBand = { minPct: 90, maxPct: 110 };
/** Apex bosses: always a step above the player — a genuine siege. */
export const APEX_BAND: PowerBand = { minPct: 120, maxPct: 160 };

/**
 * Floor applied to the player-power INPUT before banding. A brand-new colony reads as
 * a tiny militaryStrength (a handful of bodies, no genes); without a floor the whole
 * ecosystem would scale to near-nothing and read as hopelessly weak. Tuned to an
 * early-game colony's strength so the starter world matches today's hand-set baseline.
 */
export const MIN_REF_POWER = 24;

/**
 * Approximate combat "power" one average wildlife unit represents, in the same
 * militaryStrength units as the player. Converts a target wildlife-POWER into a
 * HEAD-COUNT. Tuned from defaultSpeciesConfigs (mean startHp ≈ 15, attackPower ≈ 4):
 * at the early-game floor (24) a ~100% band yields ≈ 6 units, matching the legacy
 * baseCapacity of 6.
 */
export const WILDLIFE_UNIT_POWER = 4;

/** Wildlife head-count never drops below this — the world is never empty of threats. */
export const WILDLIFE_FLOOR = 4;

/**
 * Boss hp pool per unit of player power. The siege depth (hp the player must out-damage)
 * IS the boss's felt power, so hp is the scaling lever (attackPower is left at config so
 * scaling can never make a boss one-shot a colony — i.e. never "impossible" by lethality,
 * only by endurance, which the colony can always grind down). Tuned with APEX_BAND so a
 * mid-game colony (power ≈ 100) meets a boss near its iconic hp.
 */
export const APEX_HP_PER_POWER = 2;
/** A boss is never weaker than this % of its iconic (config) hp — never a trivial boss. */
export const APEX_MIN_HP_MULT_PCT = 60;
/** Nor stronger than this % of its iconic hp — never an impossible hp wall. */
export const APEX_MAX_HP_MULT_PCT = 400;

/** Player power floored to MIN_REF_POWER and coerced to a non-negative integer. */
export function refPower(playerPower: number): number {
  const p = playerPower > 0 ? (playerPower | 0) : 0;
  return p < MIN_REF_POWER ? MIN_REF_POWER : p;
}

/**
 * A deterministic integer percent sampled UNIFORMLY across [band.minPct, band.maxPct]
 * (inclusive). This single draw IS the per-roll variance. Draws exactly one int from
 * `rng` (so callers can reason about stream consumption).
 */
export function sampleBandPct(rng: Rng, band: PowerBand): number {
  const span = band.maxPct - band.minPct + 1; // ≥ 1
  return band.minPct + rng.int(span);
}

/**
 * Target WILDLIFE head-count for the given player power: band the total wildlife power
 * to WILDLIFE_BAND × power (with per-pass variance), divide by the per-unit power, and
 * clamp to [WILDLIFE_FLOOR, hardCap]. `hardCap` is the ecosystem's maxWildlife — the
 * absolute "never an impossible swarm" ceiling (and a perf bound). Draws one int.
 */
export function wildlifeTargetCount(playerPower: number, hardCap: number, rng: Rng): number {
  const pct = sampleBandPct(rng, WILDLIFE_BAND);
  const targetPower = Math.floor((pct * refPower(playerPower)) / 100);
  let count = Math.floor(targetPower / WILDLIFE_UNIT_POWER);
  if (count < WILDLIFE_FLOOR) count = WILDLIFE_FLOOR;
  const cap = hardCap < WILDLIFE_FLOOR ? WILDLIFE_FLOOR : hardCap;
  if (count > cap) count = cap;
  return count;
}

/**
 * Scaled boss max-hp for the given player power. The MEAN boss is banded to APEX_BAND ×
 * power × APEX_HP_PER_POWER (with variance); each individual boss is then distributed
 * around that mean BY ITS OWN BASE HP (`baseMaxHp / meanBaseHp`), so the four bosses keep
 * their distinct hp identities (the tanky one stays tankiest) while the tier as a whole
 * tracks the player. Finally clamped to [base×MIN%, base×MAX%] so a boss is never weaker
 * than a fraction of its iconic hp (never trivial) nor a runaway wall (never impossible).
 * `meanBaseHp` is the mean config hp across the boss kinds (passed by the caller so it
 * can't drift from the live configs). Draws one int.
 */
export function apexScaledHp(baseMaxHp: number, meanBaseHp: number, playerPower: number, rng: Rng): number {
  const pct = sampleBandPct(rng, APEX_BAND);
  const meanTarget = Math.floor((pct * refPower(playerPower) * APEX_HP_PER_POWER) / 100);
  const mean = meanBaseHp > 0 ? meanBaseHp : baseMaxHp; // guard a degenerate mean
  let hp = Math.floor((meanTarget * baseMaxHp) / mean);
  const floor = Math.floor((baseMaxHp * APEX_MIN_HP_MULT_PCT) / 100);
  const ceil = Math.floor((baseMaxHp * APEX_MAX_HP_MULT_PCT) / 100);
  if (hp < floor) hp = floor;
  if (hp > ceil) hp = ceil;
  return hp;
}

/**
 * Rival evolution band decision vs the player, for keeping a rival colony within
 * RIVAL_BAND of player power. Returns:
 *    +1  CATCH-UP  — rival is below the band ⇒ let it evolve off its normal cadence.
 *     0  IN-BAND   — rival is within the band ⇒ evolve on its normal staggered cadence.
 *    −1  CAPPED    — rival is above the band ⇒ skip its scheduled evolution this cycle.
 * Pure + deterministic (no RNG): the band edges are crisp at RIVAL_BAND; tier variance
 * emerges from population dynamics, war rolls, and the discrete evolution steps.
 */
export function rivalBandAction(rivalPower: number, playerPower: number): number {
  const power = refPower(playerPower);
  const lo = Math.floor((RIVAL_BAND.minPct * power) / 100);
  const hi = Math.floor((RIVAL_BAND.maxPct * power) / 100);
  if (rivalPower > hi) return -1;
  if (rivalPower < lo) return 1;
  return 0;
}
